import { throttle } from 'lodash';
import { nanoid } from 'nanoid';
import { subscribe } from 'valtio';
import {
  EditorMode,
  type ImageObject,
  type StampObject,
  type TextObject,
  type TextObjectV2,
  TRANSPARENT,
} from '~/common/types';
import { BRIGHT_COLORS, PRESET_COLORS } from '~/constants/color';
import DrawArrowObject from './arrow/DrawArrowObject';
import EditArrowObject from './arrow/EditArrowObject';
import type { CanvasEvent, HoverEvent } from './CanvasEvent';
import type CanvasObject from './CanvasObject';
import EditCircleObject from './circle/EditCircleObject';
import { EditCommentObject } from './comment';
import EditImageObject from './image/EditImageObject';
import DrawLineObject from './line/DrawLineObject';
import EditLineObject from './line/EditLineObject';
import DrawMarkerObject from './marker/DrawMarkerObject';
import EditMarkerObject from './marker/EditMarkerObject';
import DrawMeasureAreaObject from './measure/DrawMeasureAreaObject';
import DrawMeasureDistanceObject from './measure/DrawMeasureDistanceObject';
import DrawPolygonObject from './polygon/DrawPolygonObject';
import EditPolygonObject from './polygon/EditPolygonObject';
import SurfaceBase, { type LngLat, type SurfaceBaseOptions } from './SurfaceBase';
import EditStampObject from './stamp/EditStampObject';
import DrawTextObject from './text/DrawTextObject';
import EditTextObject from './text/EditTextObject';
import DrawTextObjectV2 from './textV2/DrawTextObject';
import EditTextObjectV2 from './textV2/EditTextObject';

interface EditorSurfaceOptions extends SurfaceBaseOptions {}

export default class EditorSurface extends SurfaceBase {
  constructor(options: EditorSurfaceOptions) {
    super(options);
    const store = this.getStore();
    this.cleanupFns.push(
      subscribe(store, () => {
        if (store.currentObjectId && store.editorMode !== EditorMode.None) {
          this.renderAll();
        }
      }),
    );

    const PRESET_COLORS_FILTERED = PRESET_COLORS.filter(
      (color) => ![...BRIGHT_COLORS, 'transparent'].includes(color.hex),
    );

    if (store.editorMode === EditorMode.DrawPolygon) {
      const sortOrder = store.generateSortOrderKey();
      const drawPolygon = new DrawPolygonObject(nanoid(), this, function onPolygon(p: LngLat[]) {
        store.newObject({
          id: nanoid(),
          sortOrder,
          type: 'polygon',
          title: 'New Polygon',
          description: '',
          geometry: {
            type: 'Polygon',
            coordinates: [p.map((v) => [v.lng, v.lat])],
          },
          properties: {
            color: PRESET_COLORS_FILTERED[Math.floor(Math.random() * PRESET_COLORS_FILTERED.length)].hex,
            fillOpacity: 0.25,
            strokeOpacity: 1,
            strokeWidth: 4,
            strokeStyle: 'solid',
            showArea: true,
            showTitle: true,
            labelAlwaysVisible: false,
          },
        });
      });
      this.addObject(drawPolygon);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawPolygon.onSurfaceTap(e);
        this.removeEscapeListeners();
      };

      const onDoubleTap = (e: CanvasEvent) => {
        drawPolygon.onSurfaceTap(e);
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawPolygon.onPointerMove(e);
      }, 10);

      const onKeyPress = (e: KeyboardEvent) => {
        drawPolygon.onKeyPress(e);
      };

      this.on('tap', onTap);
      this.on('dblTap', onDoubleTap);
      this.on('hovering', onPointerMove);
      this.on('keyPress', onKeyPress);
    } else if (store.editorMode === EditorMode.EditPolygon) {
      if (store.currentObject) {
        const editPolygon = new EditPolygonObject(store.currentObject.id, this, function onPolygonUpdated(p: LngLat[]) {
          store.updateObject({
            geometry: {
              type: 'Polygon',
              coordinates: [p.map((p) => [p.lng, p.lat])],
            },
          });
        });
        this.addHoverListeners(editPolygon);
        this.addObject(editPolygon);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.DrawLine) {
      const sortOrder = store.generateSortOrderKey();
      const drawLine = new DrawLineObject(nanoid(), this, function onLine(p: LngLat[]) {
        const id = nanoid();
        store.newObject({
          id,
          sortOrder,
          type: 'line',
          title: 'New Line',
          description: '',
          geometry: {
            type: 'LineString',
            coordinates: p.map((v) => [v.lng, v.lat]),
          },
          properties: {
            color: PRESET_COLORS_FILTERED[Math.floor(Math.random() * PRESET_COLORS_FILTERED.length)].hex,
            strokeOpacity: 1,
            strokeWidth: 4,
            strokeStyle: 'solid',
            showLength: true,
            showTitle: true,
            labelAlwaysVisible: false,
          },
        });
      });
      this.addObject(drawLine);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawLine.onSurfaceTap(e);
        this.removeEscapeListeners();
      };

      const onDoubleTap = (e: CanvasEvent) => {
        drawLine.onSurfaceTap(e);
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawLine.onPointerMove(e);
      }, 10);

      const onKeyPress = (e: KeyboardEvent) => {
        drawLine.onKeyPress(e);
      };

      this.on('tap', onTap);
      this.on('dblTap', onDoubleTap);
      this.on('hovering', onPointerMove);
      this.on('keyPress', onKeyPress);
    } else if (store.editorMode === EditorMode.EditLine) {
      if (store.currentObject) {
        const editLine = new EditLineObject(store.currentObject.id, this, function onLineUpdated(p: LngLat[]) {
          store.updateObject({
            geometry: {
              type: 'LineString',
              coordinates: p.map((p) => [p.lng, p.lat]),
            },
          });
        });
        this.addHoverListeners(editLine);
        this.addObject(editLine);
        this.on('tap', editLine.onSurfaceTap);
        this.on('dblTap', editLine.onSurfaceTap);
      }
    } else if (store.editorMode === EditorMode.DrawMarker) {
      const sortOrder = store.generateSortOrderKey();
      const map = this.map;
      const drawMarker = new DrawMarkerObject(nanoid(), this, function onMarker(p: LngLat) {
        store.newObject(
          {
            id: nanoid(),
            sortOrder,
            type: 'marker',
            title: 'New Marker',
            description: '',
            geometry: {
              type: 'Point',
              coordinates: [p.lng, p.lat],
            },
            properties: {
              color: PRESET_COLORS_FILTERED[Math.floor(Math.random() * PRESET_COLORS_FILTERED.length)].hex,
              showTitle: true,
            },
          },
          undefined,
          map,
        );
      });
      this.addObject(drawMarker);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawMarker.onSurfaceTap(e);
        this.removeEscapeListeners();
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawMarker.onPointerMove(e);
      }, 10);

      this.on('tap', onTap);
      this.on('hovering', onPointerMove);
    } else if (store.editorMode === EditorMode.EditMarker) {
      if (store.currentObject) {
        const editMarker = new EditMarkerObject(store.currentObject.id, this, function onMarkerUpdated(p: LngLat) {
          store.updateObject({
            geometry: {
              type: 'Point',
              coordinates: [p.lng, p.lat],
            },
          });
        });
        this.addHoverListeners(editMarker);
        this.addObject(editMarker);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.DrawText) {
      const sortOrder = store.generateSortOrderKey();
      const drawText = new DrawTextObject(Math.random().toString(), this, (p: LngLat) => {
        store.newObject({
          id: nanoid(),
          sortOrder,
          type: 'text',
          title: 'New Text',
          description: 'Important\nnotes\ngo here 🚩 ',
          geometry: {
            type: 'Point',
            coordinates: [p.lng, p.lat],
          },
          properties: {
            color: '#000000',
            backgroundColor: TRANSPARENT,
            fontSize: 24,
            createdAtZoom: this.map.getZoom(),
            rotation: 0,
          },
        });
      });
      this.addObject(drawText);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawText.onSurfaceTap(e);
        this.removeEscapeListeners();
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawText.onPointerMove(e);
      }, 10);

      this.on('tap', onTap);
      this.on('hovering', onPointerMove);
    } else if (store.editorMode === EditorMode.EditText) {
      if (store.currentObject) {
        const editText = new EditTextObject(
          store.currentObject.id,
          this,
          store.currentObject as TextObject,
          function onTextUpdated(p: LngLat) {
            store.updateObject({
              geometry: {
                type: 'Point',
                coordinates: [p.lng, p.lat],
              },
            });
          },
        );
        this.addHoverListeners(editText);
        this.addObject(editText);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.EditComment) {
      if (store.currentObject) {
        const editComment = new EditCommentObject(store.currentObject.id, this, function onMarkerUpdated(p: LngLat) {
          store.updateObject({
            geometry: {
              type: 'Point',
              coordinates: [p.lng, p.lat],
            },
          });
        });
        this.addHoverListeners(editComment);
        this.addObject(editComment);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.DrawTextV2) {
      const sortOrder = store.generateSortOrderKey();
      const drawText = new DrawTextObjectV2(nanoid(), this, (p: LngLat[], description: string) => {
        store.newObject({
          id: nanoid(),
          sortOrder,
          type: 'textV2',
          title: 'Text Area',
          description,
          geometry: {
            type: 'Polygon',
            coordinates: [p.map((p) => [p.lng, p.lat])],
          },
          properties: {
            color: '#000000',
            backgroundColor: TRANSPARENT,
            borderColor: TRANSPARENT,
          },
        });
      });
      this.addObject(drawText);
      this.setCursor('crosshair');
      const onTap = (e: CanvasEvent) => {
        drawText.onSurfaceTap(e);
        this.removeEscapeListeners();
      };
      const onPointerMove = throttle((e: HoverEvent) => {
        drawText.onPointerMove(e);
      }, 10);

      this.on('tap', onTap);
      this.on('hovering', onPointerMove);
    } else if (store.editorMode === EditorMode.EditTextV2) {
      if (store.currentObject) {
        const editText = new EditTextObjectV2(
          store.currentObject.id,
          this,
          store.currentObject as TextObjectV2,
          function onTextUpdated(p: LngLat[], description: string) {
            store.updateObject({
              description,
              geometry: {
                type: 'Polygon',
                coordinates: [p.map((p) => [p.lng, p.lat])],
              },
            });
          },
        );

        this.addHoverListeners(editText);
        this.addObject(editText);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.EditImage) {
      if (store.currentObject) {
        const editText = new EditImageObject(
          store.currentObject.id,
          this,
          store.currentObject as ImageObject,
          function onImageUpdated(p: LngLat[]) {
            store.updateObject({
              geometry: {
                type: 'Polygon',
                coordinates: [p.map((p) => [p.lng, p.lat])],
              },
            });
          },
        );

        this.addHoverListeners(editText);
        this.addObject(editText);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.EditStamp) {
      if (store.currentObject) {
        const editStamp = new EditStampObject(
          store.currentObject.id,
          this,
          store.currentObject as StampObject,
          function onImageUpdated(p: LngLat[]) {
            store.updateObject({
              geometry: {
                type: 'Polygon',
                coordinates: [p.map((p) => [p.lng, p.lat])],
              },
            });
          },
        );
        this.addHoverListeners(editStamp);
        this.addObject(editStamp);
      }
      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.DrawMeasureDistance) {
      const drawMeasureDistance = new DrawMeasureDistanceObject(
        nanoid(),
        this,
        function onMeasureDistance(p: LngLat[]) {},
      );

      this.addObject(drawMeasureDistance);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawMeasureDistance.onSurfaceTap(e);
      };

      const onDoubleTap = (e: CanvasEvent) => {
        drawMeasureDistance.onSurfaceTap(e);
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawMeasureDistance.onPointerMove(e);
      }, 10);

      const onKeyPress = (e: KeyboardEvent) => {
        drawMeasureDistance.onKeyPress(e);
      };

      this.on('tap', onTap);
      this.on('dblTap', onDoubleTap);
      this.on('hovering', onPointerMove);
      this.on('keyPress', onKeyPress);
    } else if (store.editorMode === EditorMode.DrawMeasureArea) {
      const drawMeasureArea = new DrawMeasureAreaObject(nanoid(), this, function onMeasureArea() {});

      this.addObject(drawMeasureArea);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawMeasureArea.onSurfaceTap(e);
      };

      const onDoubleTap = (e: CanvasEvent) => {
        drawMeasureArea.onSurfaceTap(e);
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawMeasureArea.onPointerMove(e);
      }, 10);

      const onKeyPress = (e: KeyboardEvent) => {
        drawMeasureArea.onKeyPress(e);
      };

      this.on('tap', onTap);
      this.on('dblTap', onDoubleTap);
      this.on('hovering', onPointerMove);
      this.on('keyPress', onKeyPress);
    } else if (store.editorMode === EditorMode.DrawArrow) {
      const sortOrder = store.generateSortOrderKey();

      const drawArrow = new DrawArrowObject(nanoid(), this, function onArrow(p: LngLat[]) {
        const id = nanoid();
        store.newObject({
          id,
          sortOrder,
          type: 'arrow',
          title: 'New Arrow',
          description: 'Use this space for\ndetailed description',
          geometry: {
            type: 'LineString',
            coordinates: p.map((v) => [v.lng, v.lat]),
          },
          properties: {
            subtype: 'standard',
            color: PRESET_COLORS_FILTERED[Math.floor(Math.random() * PRESET_COLORS_FILTERED.length)].hex,
            strokeOpacity: 1,
            strokeWidth: 2,
            strokeStyle: 'solid',
            showTitle: true,
            showDescription: false,
            labelAlwaysVisible: false,
            showAsLeader: false,
          },
        });
      });

      this.addObject(drawArrow);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawArrow.onSurfaceTap(e);
        this.removeEscapeListeners();
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawArrow.onPointerMove(e);
      });

      const onKeyPress = (e: KeyboardEvent) => {
        drawArrow.onKeyPress(e);
      };

      this.on('tap', onTap);
      this.on('hovering', onPointerMove);
      this.on('keyPress', onKeyPress);
    } else if (store.editorMode === EditorMode.EditArrow) {
      if (store.currentObject) {
        const editArrow = new EditArrowObject(store.currentObject.id, this, function onArrowUpdated(p: LngLat[]) {
          store.updateObject({
            geometry: {
              type: 'LineString',
              coordinates: p.map((p) => [p.lng, p.lat]),
            },
          });
        });
        this.addHoverListeners(editArrow);
        this.addObject(editArrow);
      }

      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    } else if (store.editorMode === EditorMode.DrawCircle) {
      const drawCircle = new DrawMarkerObject(nanoid(), this, function onCircle(p: LngLat) {
        const { lng, lat } = p;
        store.newObject({
          id: nanoid(),
          sortOrder: store.generateSortOrderKey(),
          type: 'circle',
          title: 'New Circle',
          description: '',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat],
          },
          properties: {
            radius: 50,
            color: PRESET_COLORS_FILTERED[Math.floor(Math.random() * PRESET_COLORS_FILTERED.length)].hex,
            fillOpacity: 0.25,
            strokeOpacity: 1,
            strokeWidth: 2,
            strokeStyle: 'solid',
            showArea: true,
            showRadius: true,
            showTitle: true,
            labelAlwaysVisible: false,
          },
        });
      });

      this.addObject(drawCircle);
      this.setCursor('crosshair');

      const onTap = (e: CanvasEvent) => {
        drawCircle.onSurfaceTap(e);
        this.removeEscapeListeners();
      };

      const onPointerMove = throttle((e: HoverEvent) => {
        drawCircle.onPointerMove(e);
      });

      this.on('tap', onTap);
      this.on('hovering', onPointerMove);
    } else if (store.editorMode === EditorMode.EditCircle) {
      if (store.currentObject) {
        const editCircle = new EditCircleObject(store.currentObject.id, this, function onCircleUpdated(p: LngLat) {
          const { lng, lat } = p;
          store.updateObject({
            geometry: {
              type: 'Point',
              coordinates: [lng, lat],
            },
          });
        });

        this.addHoverListeners(editCircle);
        this.addObject(editCircle);
      }

      this.on('tap', () => {
        store.resetCurrentObjectId();
      });
    }

    this.resetCursor();
    addEventListener('keyup', this.onEscape);
  }

  private addHoverListeners = (o: CanvasObject) => {
    o.on('hoverStart', () => {
      this.setCursor('all-scroll');
    });
    o.on('hoverEnd', () => {
      this.setCursor('default');
    });
  };

  override resetCursor = () => {
    this.canvas.style.cursor = 'crosshair';
  };

  // For when the user presses the escape after selecting the object from the toolbar but before placing it on the map (initial click).
  private onEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.getStore().resetCurrentObjectId();
      this.removeEscapeListeners();
      return;
    }
  };

  removeEscapeListeners = () => {
    removeEventListener('keyup', this.onEscape);
  };
}
